import React, { useState } from 'react';
import { 
  ParentalConsentData, 
  validateParentalConsent, 
  generateParentalConsentCode,
  getParentalConsentRequirements 
} from '../utils/ageVerification';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface ParentalConsentProps {
  onConsentGiven: (consentData: ParentalConsentData) => void;
  onConsentDenied: () => void;
  childName: string;
  childAge: number;
}

const ParentalConsent: React.FC<ParentalConsentProps> = ({
  onConsentGiven,
  onConsentDenied,
  childName,
  childAge
}) => {
  const [consentData, setConsentData] = useState<Partial<ParentalConsentData>>({
    parentEmail: '',
    parentName: '',
    consentGiven: false,
    consentDate: new Date().toISOString()
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setConsentData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const sendVerificationEmail = async () => {
    if (!consentData.parentEmail || !consentData.parentName) {
      setEmailError('Please fill in parent email and name first');
      return;
    }

    setSendingEmail(true);
    setEmailError('');

    try {
      // Generate verification code
      const code = generateParentalConsentCode();
      setVerificationCode(code);

      // Send verification email
      const sendParentalConsentVerificationEmail = httpsCallable(functions, 'sendParentalConsentVerificationEmail');
      await sendParentalConsentVerificationEmail({
        parentEmail: consentData.parentEmail,
        parentName: consentData.parentName,
        childName: childName,
        childAge: childAge,
        verificationCode: code
      });

      setEmailSent(true);
    } catch (error) {
      console.error('Error sending verification email:', error);
      setEmailError('Failed to send verification email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!consentData.consentGiven) {
      setErrors(['You must provide consent to proceed']);
      return;
    }

    if (!emailSent) {
      setErrors(['Please verify your email address first']);
      return;
    }

    if (!verificationCode) {
      setErrors(['Please enter the verification code from your email']);
      return;
    }

    const validation = validateParentalConsent(consentData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Create final consent data
      const finalConsentData: ParentalConsentData = {
        parentEmail: consentData.parentEmail!,
        parentName: consentData.parentName!,
        consentGiven: true,
        consentDate: new Date().toISOString(),
        verificationCode: verificationCode
      };
      
      // Send confirmation email
      try {
        const sendParentalConsentConfirmationEmail = httpsCallable(functions, 'sendParentalConsentConfirmationEmail');
        await sendParentalConsentConfirmationEmail({
          parentEmail: consentData.parentEmail!,
          parentName: consentData.parentName!,
          childName: childName,
          childAge: childAge
        });
      } catch (emailError) {
        console.warn('Failed to send confirmation email:', emailError);
        // Don't fail the whole process if email fails
      }
      
      onConsentGiven(finalConsentData);
    } catch (error) {
      console.error('Error processing consent:', error);
      setErrors(['Failed to process consent. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div className="bg-white shadow-xl rounded-lg p-8">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Parental Consent Required
            </h2>
            <p className="text-lg text-gray-600">
              Your child <strong>{childName}</strong> (age {childAge}) requires parental consent to use this service.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-yellow-800 mb-3">
              ⚠️ Important Privacy Notice
            </h3>
            <div className="text-sm text-yellow-700 space-y-2">
              <p>
                Under the Children's Online Privacy Protection Act (COPPA), we must obtain 
                verifiable parental consent before collecting personal information from children under 13.
              </p>
              <p>
                We collect limited information necessary to provide our music lesson service, 
                including your child's name, instrument preferences, and practice data.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="parentName" className="block text-sm font-medium text-gray-700 mb-2">
                Parent/Guardian Full Name *
              </label>
              <input
                id="parentName"
                name="parentName"
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter your full name"
                value={consentData.parentName || ''}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label htmlFor="parentEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Parent/Guardian Email Address *
              </label>
              <input
                id="parentEmail"
                name="parentEmail"
                type="email"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter your email address"
                value={consentData.parentEmail || ''}
                onChange={handleInputChange}
              />
              <p className="mt-1 text-sm text-gray-500">
                We'll send a verification code to this email address
              </p>
            </div>

            {/* Email Verification Section */}
            {!emailSent ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-yellow-800 mb-2">
                  Step 1: Verify Your Email Address
                </h4>
                <p className="text-sm text-yellow-700 mb-3">
                  Click the button below to send a verification code to your email address. 
                  This ensures you are the legitimate parent/guardian.
                </p>
                <button
                  type="button"
                  onClick={sendVerificationEmail}
                  disabled={sendingEmail || !consentData.parentEmail || !consentData.parentName}
                  className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {sendingEmail ? 'Sending...' : 'Send Verification Code'}
                </button>
                {emailError && (
                  <p className="mt-2 text-sm text-red-600">{emailError}</p>
                )}
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-green-800 mb-2">
                  ✅ Email Verification Sent
                </h4>
                <p className="text-sm text-green-700 mb-3">
                  We've sent a verification code to <strong>{consentData.parentEmail}</strong>. 
                  Please check your email and enter the code below.
                </p>
                <div>
                  <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code
                  </label>
                  <input
                    id="verificationCode"
                    name="verificationCode"
                    type="text"
                    required
                    placeholder="Enter 6-digit code"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    maxLength={6}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Didn't receive the email? Check your spam folder or{' '}
                    <button
                      type="button"
                      onClick={sendVerificationEmail}
                      disabled={sendingEmail}
                      className="text-indigo-600 hover:text-indigo-500 underline"
                    >
                      resend
                    </button>
                  </p>
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">
                Your Rights as a Parent/Guardian:
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Review all personal information collected about your child</li>
                <li>• Request deletion of your child's personal information</li>
                <li>• Refuse further collection of personal information</li>
                <li>• Revoke consent at any time</li>
              </ul>
            </div>

            <div className="space-y-4">
              <div className="flex items-start">
                <input
                  id="consentGiven"
                  name="consentGiven"
                  type="checkbox"
                  required
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  checked={consentData.consentGiven || false}
                  onChange={handleInputChange}
                />
                <label htmlFor="consentGiven" className="ml-3 text-sm text-gray-700">
                  <span className="font-medium">I consent to the collection of personal information</span>
                  <br />
                  <span className="text-gray-500">
                    I am the parent/guardian of {childName} and have the legal right to provide consent on their behalf. 
                    I understand that I can revoke this consent at any time by contacting us.
                  </span>
                </label>
              </div>

              <div className="flex items-start">
                <input
                  id="privacyPolicy"
                  name="privacyPolicy"
                  type="checkbox"
                  required
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="privacyPolicy" className="ml-3 text-sm text-gray-700">
                  <span className="font-medium">I have read and agree to the </span>
                  <a 
                    href="/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-500 underline font-medium"
                  >
                    Privacy Policy
                  </a>
                  <br />
                  <span className="text-gray-500">
                    I understand how my child's information will be collected, used, and protected.
                  </span>
                </label>
              </div>

              <div className="flex items-start">
                <input
                  id="termsOfUse"
                  name="termsOfUse"
                  type="checkbox"
                  required
                  className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="termsOfUse" className="ml-3 text-sm text-gray-700">
                  <span className="font-medium">I have read and agree to the </span>
                  <a 
                    href="/terms-of-use" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-500 underline font-medium"
                  >
                    Terms of Use
                  </a>
                  <br />
                  <span className="text-gray-500">
                    I understand the terms and conditions for using this service.
                  </span>
                </label>
              </div>
            </div>

            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <ul className="text-sm text-red-600 space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onConsentDenied}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 px-4 rounded-md font-medium transition-colors"
              >
                Deny Consent
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !emailSent}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white py-3 px-4 rounded-md font-medium transition-colors"
              >
                {isSubmitting ? 'Processing...' : emailSent ? 'Complete Consent' : 'Verify Email First'}
              </button>
            </div>
          </form>

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              By providing consent, you acknowledge that you have read and understood our 
              data collection practices and your rights as a parent/guardian.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentalConsent;
